import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { T, useTranslation } from "@redotech/redo-i18n";
import LaptopIcon from "@redotech/redo-icons/laptop-01.svg";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { useIsTabletOrSmaller } from "@redotech/redo-merchant-fulfillment-app/mobile-launcher/mobile-launcher";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { shouldShowDesktopHandoff } from "./eligibility";

/** Matches the server's limit on resends. */
const RESEND_COOLDOWN_SECONDS = 60;

const continuedOnMobileKey = (userId: string) =>
  `redo.desktop-handoff-continued-on-mobile.${userId}`;

function readContinuedOnMobile(userId: string | undefined): boolean {
  if (!userId) {
    return false;
  }
  try {
    return localStorage.getItem(continuedOnMobileKey(userId)) === "true";
  } catch {
    return false;
  }
}

export const DesktopHandoffGate = memo(function DesktopHandoffGate({
  children,
}: {
  children: ReactNode;
}) {
  const auth = useContext(AuthContext);
  const team = useRequiredContext(TeamContext);
  const user = useContext(UserContext);
  const location = useLocation();
  const isSmallScreen = useIsTabletOrSmaller();
  const [continuedOnMobile, setContinuedOnMobile] = useState(() =>
    readContinuedOnMobile(user?._id),
  );

  const visible = shouldShowDesktopHandoff({
    isSmallScreen,
    continuedOnMobile,
    omsEnabled: team.settings.outboundLabels?.newOmsEnabled === true,
    isAdminImpersonation: auth?.isAdminImpersonation,
    user,
    pathname: location.pathname,
    now: Date.now(),
  });

  if (!visible || !user) {
    return <>{children}</>;
  }

  const continueOnMobile = () => {
    try {
      localStorage.setItem(continuedOnMobileKey(user._id), "true");
    } catch {
      // Without storage the choice lasts only for this page load.
    }
    setContinuedOnMobile(true);
  };

  return (
    <DesktopHandoffScreen
      email={user.email}
      nextPath={`${location.pathname}${location.search}`}
      onContinueOnMobile={continueOnMobile}
    >
      {children}
    </DesktopHandoffScreen>
  );
});

const DesktopHandoffScreen = memo(function DesktopHandoffScreen({
  email,
  nextPath,
  onContinueOnMobile,
  children,
}: {
  email: string;
  nextPath: string;
  onContinueOnMobile: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  // Pinned to the first page so navigating behind this screen doesn't re-send.
  const [loginNextPath] = useState(nextPath);
  const sendLoad = useLoad(
    (signal) =>
      rpcClient.sendDesktopLoginLink({ nextPath: loginNextPath }, { signal }),
    [rpcClient, loginNextPath],
  );

  const [cooldown, setCooldown] = useState(0);
  const [resend, setResend] = useState<"idle" | "sending" | "sent" | "failed">(
    "idle",
  );

  // Each send, automatic or not, starts the countdown before the next resend.
  useEffect(() => {
    if (sendLoad.value?.sent) {
      setCooldown(RESEND_COOLDOWN_SECONDS);
    }
  }, [sendLoad.value]);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const resendEmail = async () => {
    setResend("sending");
    try {
      const result = await rpcClient.sendDesktopLoginLink({
        nextPath: loginNextPath,
        resend: true,
      });
      setResend(result.sent ? "sent" : "idle");
    } catch {
      setResend("failed");
    }
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  // useLoad reports neither pending nor a result before its first request, so
  // wait for an answer instead of flashing the app this screen replaces.
  if (
    sendLoad.pending ||
    (sendLoad.value === undefined && sendLoad.error === undefined)
  ) {
    return null;
  }
  // The server decides who self-onboarded to OMS. If the send failed, still
  // point the merchant to their computer rather than dropping them into the app.
  const sendFailed = sendLoad.error !== undefined;
  if (!sendFailed && !sendLoad.value?.eligible) {
    return <>{children}</>;
  }
  const credit = sendLoad.value?.credit ?? null;

  return (
    <Flex
      align="center"
      dir="column"
      gap="xl"
      justify="center"
      minH="screen"
      px="2xl"
      py="4xl"
    >
      <RedoFeaturedIcon
        color="black"
        Icon={LaptopIcon}
        shape="circle"
        size="lg"
      />
      <Flex align="center" dir="column" gap="md">
        <Text fontSize="xl" fontWeight="semibold" textAlign="center">
          {t("Welcome to Redo!")}
        </Text>
        <Text fontSize="md" textAlign="center" textColor="secondary">
          {sendFailed ? (
            t(
              "Redo's OMS is best experienced on a larger screen. Log in to Redo on your computer to finish setting up.",
            )
          ) : (
            <T
              components={{
                email: (
                  <Text as="span" fontWeight="semibold" textColor="primary">
                    {null}
                  </Text>
                ),
              }}
              i18nKey="Redo's OMS is best experienced on a larger screen. We emailed a link to <email>{{email}}</email> so you can log in on your computer with one click."
              values={{ email }}
            />
          )}
        </Text>
        {credit && (
          <Text fontSize="md" textAlign="center" textColor="secondary">
            {t(
              "Ship your first {{count}} orders with Redo and earn up to ${{capUsd}} in shipping credit.",
              { count: credit.requiredFulfillments, capUsd: credit.capUsd },
            )}
          </Text>
        )}
      </Flex>
      {!sendFailed && (
        <Flex align="center" dir="column" gap="xs">
          <RedoButton
            disabled={resend === "sending" || cooldown > 0}
            hierarchy="secondary"
            onClick={() => void resendEmail()}
            pending={resend === "sending"}
            text={
              cooldown > 0
                ? t("Resend email ({{seconds}}s)", { seconds: cooldown })
                : t("Resend email")
            }
          />
          {resend === "sent" && (
            <Text fontSize="sm" textAlign="center" textColor="secondary">
              {t("We sent a new link. It can take a minute to arrive.")}
            </Text>
          )}
          {resend === "failed" && (
            <Text fontSize="sm" textAlign="center" textColor="error">
              {t("We couldn't send the email. Try again in a minute.")}
            </Text>
          )}
        </Flex>
      )}
      <RedoButton
        hierarchy="link"
        onClick={onContinueOnMobile}
        text={t("Continue on mobile anyway")}
      />
    </Flex>
  );
});
